import React, { useState, useLayoutEffect } from 'react';

const PADDING = 8; // px ring around the anchor

// Renders a fixed-position overlay that dims the page except for an
// element-shaped cutout around the target. Pulses an orange glow ring.
// pointer-events: none on the backdrop so the spotlighted element stays clickable.
export default function TourSpotlight({ targetEl }) {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!targetEl) { setRect(null); return; }

    const measure = () => {
      const r = targetEl.getBoundingClientRect();
      setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(targetEl);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [targetEl]);

  if (!rect) {
    // No target: dim full screen
    return (
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[80]"
        style={{ pointerEvents: 'none' }}
      />
    );
  }

  const x = rect.x - PADDING;
  const y = rect.y - PADDING;
  const w = rect.w + PADDING * 2;
  const h = rect.h + PADDING * 2;

  // 4-rectangle cutout via clip-path polygon: outer rectangle minus inner rectangle.
  return (
    <>
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[80]"
        style={{
          clipPath: `polygon(
            0 0, 100% 0, 100% 100%, 0 100%, 0 0,
            ${x}px ${y}px,
            ${x}px ${y + h}px,
            ${x + w}px ${y + h}px,
            ${x + w}px ${y}px,
            ${x}px ${y}px
          )`,
          pointerEvents: 'none',
        }}
      />
      {/* Pulsing orange glow ring around the cutout */}
      <div
        className="fixed z-[80] rounded-md pointer-events-none animate-pulse"
        style={{
          left: x,
          top: y,
          width: w,
          height: h,
          boxShadow: '0 0 24px 4px rgba(255, 138, 0, 0.6)',
          border: '2px solid rgba(255, 138, 0, 0.8)',
        }}
      />
    </>
  );
}
